import { useCallback, useEffect, useState } from 'react'
import { adminAction, getAdminState } from '../../lib/api'
import type { AdminAction } from '../../event/state'
import type { EventState } from '../../event/types'

export type AdminMessage = { kind: 'error' | 'ok'; text: string }

export type AdminEvent = {
  state: EventState | null
  message: AdminMessage | null
  busy: boolean
  /** Sends one action; resolves true when it was saved. */
  act: (action: AdminAction, done?: string) => Promise<boolean>
  clearMessage: () => void
}

/**
 * The saved event document for the administrator screens. Every view reads
 * the same copy, and every change goes through `act`, which always adopts the
 * server's answer, so a refused or conflicting change shows what is saved.
 */
export function useAdminEvent(): AdminEvent {
  const [state, setState] = useState<EventState | null>(null)
  const [message, setMessage] = useState<AdminMessage | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    getAdminState()
      .then(setState)
      .catch(() => setMessage({ kind: 'error', text: 'The event state could not be loaded.' }))
  }, [])

  const act = useCallback(
    async (action: AdminAction, done?: string) => {
      if (!state) return false
      setBusy(true)
      try {
        const result = await adminAction(state.revision, action)
        setState(result.state)
        setMessage(result.error ? { kind: 'error', text: result.error } : done ? { kind: 'ok', text: done } : null)
        return !result.error
      } catch (error) {
        setMessage({ kind: 'error', text: error instanceof Error ? error.message : 'The change was not saved.' })
        return false
      } finally {
        setBusy(false)
      }
    },
    [state],
  )

  const clearMessage = useCallback(() => setMessage(null), [])

  return { state, message, busy, act, clearMessage }
}
